import { useNavigate } from 'react-router-dom';
import { RefreshCw, Users, Building2, AlertTriangle, Globe, FileText } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useHoldingFiliales } from '@/hooks/useHoldingUsers';
import { extractError } from '@/api/client';

const PAYS_FLAG: Record<string, string> = {
  "Côte d'Ivoire": '🇨🇮',
  'Burkina Faso':  '🇧🇫',
  'Guinée':        '🇬🇳',
  'Sénégal':       '🇸🇳',
};

const METIER_BADGES: Record<string, { bg: string; color: string }> = {
  beton:            { bg: 'rgba(16,185,129,0.12)', color: '#065f46' },
  transport_minier: { bg: 'rgba(245,158,11,0.12)', color: '#92400e' },
  impression:       { bg: 'rgba(168,85,247,0.12)', color: '#6b21a8' },
  immobilier:       { bg: 'rgba(59,130,246,0.12)', color: '#1e40af' },
  telecom_wifi:     { bg: 'rgba(236,72,153,0.12)', color: '#831843' },
  generic:          { bg: 'rgba(107,114,128,0.12)', color: '#374151' },
};

/**
 * Fiches sociétés (lot 1, étape 1.7) — les 8 sociétés du groupe, actives et dormantes, avec l'accès à leurs
 * mentions légales. Ouvrir une société se fait désormais par le sélecteur de la barre de gauche (le bouton
 * « Voir » de l'ancienne page « Accès filiales » a disparu).
 */
export const FilialesAccessPage = () => {
  const { data: filiales = [], isLoading, refetch, error } = useHoldingFiliales();
  const navigate = useNavigate();

  return (
    <>
      <Topbar
        title="Fiches sociétés"
        subtitle="Identité et mentions légales des sociétés du groupe — pour ouvrir une société, utilisez le sélecteur en haut à gauche"
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">Erreur chargement : {extractError(error).message}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Sociétés</div>
            <div className="ds-ks-value">{filiales.length}</div>
            <div className="ds-ks-sub">{filiales.filter((f) => f.statut === 'dormante').length} dormante(s), pas encore lancée(s)</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Actives</div>
            <div className="ds-ks-value">{filiales.filter((f) => f.isActive).length}</div>
            <div className="ds-ks-sub">Opérationnelles</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Avec données</div>
            <div className="ds-ks-value">{filiales.filter((f) => f.stats.hasBusinessData).length}</div>
            <div className="ds-ks-sub">Au moins 1 contact</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Total utilisateurs</div>
            <div className="ds-ks-value">{filiales.reduce((s, f) => s + f.stats.usersCount, 0)}</div>
            <div className="ds-ks-sub">Toutes sociétés</div>
          </div>
        </div>

        {/* Grille filiales en cards */}
        {isLoading && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>
            <RefreshCw size={20} className="animate-spin" style={{ display: 'inline' }} /> Chargement multi-tenant…
          </div>
        )}

        {!isLoading && filiales.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>
            <Building2 size={28} /> Aucune filiale visible
          </div>
        )}

        {!isLoading && filiales.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
          }}>
            {filiales.map((f) => {
              const badge = METIER_BADGES[f.metier] ?? METIER_BADGES.generic;
              return (
                <div
                  key={f.id}
                  style={{
                    padding:      16,
                    borderRadius: 8,
                    background:   'var(--surface, white)',
                    border:       `1px solid ${f.isActive ? f.colorPrimary + '33' : 'var(--bdr)'}`,
                    borderLeft:   `4px solid ${f.colorPrimary}`,
                    boxShadow:    '0 1px 3px rgba(0,0,0,0.05)',
                    opacity:      f.isActive ? 1 : 0.6,
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1, #111)' }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 2 }}>{f.fullName}</div>
                    </div>
                    <span style={{
                      padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600,
                      background: badge.bg, color: badge.color,
                    }}>
                      {f.metierLabel}
                    </span>
                  </div>

                  {/* Meta */}
                  <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 11, color: 'var(--tx-2)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Globe size={12} /> {PAYS_FLAG[f.pays] ?? '🌍'} {f.pays}
                    </span>
                    <span>{f.devise}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Users size={12} /> {f.stats.usersCount}
                    </span>
                  </div>

                  {/* Status pill */}
                  <div style={{ marginTop: 10 }}>
                    {f.statut === 'dormante' && (
                      <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 10, background: 'rgba(107,114,128,0.12)', color: '#374151' }}>
                        Dormante — pas encore lancée
                      </span>
                    )}
                    {f.isActive && !f.stats.hasBusinessData && (
                      <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 10, background: 'rgba(245,158,11,0.1)', color: '#92400e' }}>
                        ⓘ Aucune donnée business
                      </span>
                    )}
                    {f.isActive && f.stats.hasBusinessData && (
                      <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 10, background: 'rgba(16,185,129,0.1)', color: '#065f46' }}>
                        ✓ Active avec données
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ marginTop: 14, display: 'flex', gap: 6 }}>
                    {/* SIMGROUP-2026-003 Sprint 1 — accès direct mentions légales OHADA */}
                    <button
                      type="button"
                      className="ds-btn ds-btn-g ds-btn-sm"
                      onClick={() => navigate(`/holding/filiales/${f.filialeKey}/legal`)}
                      title="Éditer les mentions légales OHADA (RCCM, NCC, dirigeant…)"
                      style={{ justifyContent: 'center', flex: 1 }}
                    >
                      <FileText size={13} /> Mentions légales
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
